import React from "react";
import { Modal } from "react-responsive-modal";
import styles from "./ImageGallery.module.scss";
import "react-responsive-modal/styles.css";
import { CloudinaryImage, CloudinaryVideo } from "../../Atoms/Cloudinary";
import { FaTh } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// import required modules
import { Pagination, Navigation } from "swiper/modules";
import { MediaCategory } from "../../../graphql/operations";

interface GalleryProps {
  isProductPage?: boolean;
  setGalleryOpen: Function;
  openGallery: boolean;
  photos: {
    url: string;
    type: string;
    category?: MediaCategory;
    id?: string;
  }[];
  videos?: {
    url: string;
    category?: MediaCategory;
    type: string;
  }[];
}

const PlanImageGallery = ({
  photos,
  openGallery,
  setGalleryOpen,
  isProductPage,
  videos,
}: GalleryProps) => {
  const [categoryFilter, setCategoryFilter] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [openSecond, setOpenSecond] = React.useState<{
    state: boolean;
    index: undefined | number;
  }>({
    state: false,
    index: undefined,
  });
  React.useEffect(() => {
    setOpen(openGallery);
  }, [openGallery]);

  const categories = [];

  for (let i = 0; i < photos.length; i++) {
    if (
      photos[i].category !== null &&
      categories.filter((c) => c?.id === photos[i].category?.id).length === 0
    ) {
      categories.push(photos[i].category);
    }
  }

  const filteredPhotos =
    categoryFilter !== null
      ? photos.filter((p) => p.category?.id === categoryFilter)
      : photos;

  return (
    <>
      <div
        onClick={() => setGalleryOpen(true)}
        className={styles["BorderButton"]}
      >
        <FaTh
          size={20}
          style={{
            marginRight: 7,
          }}
        />
        <span
          style={{
            fontSize: 12,
            fontFamily: "sans-serif",
          }}
        >
          View All Photos
        </span>
      </div>
      <Modal
        open={open}
        onClose={() => setGalleryOpen(false)}
        classNames={{
          overlay: styles["customGalleryOverlay"],
          modal: styles["customGalleryModal"],
        }}
        showCloseIcon={!isProductPage}
        blockScroll={false}
      >
        {isProductPage && (
          <div className={styles["GalleryNav"]}>
            <button
              onClick={() => setGalleryOpen(false)}
              className="react-responsive-modal-closeButton"
              data-testid="close-button"
              style={{
                marginTop: 0,
                marginRight: 10,
                float: "right",
                position: "static",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 36 36"
                data-testid="close-icon"
              >
                <path d="M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z"></path>
              </svg>
            </button>

            <ul className={styles["PhotoSortTabs"]}>
              <li
                className={
                  categoryFilter === null && styles["isActiveCategory"]
                }
                onClick={() => setCategoryFilter(null)}
                onTouchEnd={() => setCategoryFilter(null)}
              >
                All <span>({photos.length})</span>
              </li>
              {videos && videos.length > 0 && (
                <li
                  className={
                    categoryFilter === "videos" && styles["isActiveCategory"]
                  }
                  onClick={() => setCategoryFilter("videos")}
                  onTouchEnd={() => setCategoryFilter("videos")}
                >
                  Videos
                  <span>({videos.length})</span>
                </li>
              )}
              {categories
                .sort(function (a, b) {
                  return a.name.localeCompare(b.name);
                })
                .map((c, index) => {
                  return (
                    <li
                      className={
                        categoryFilter === c.id && styles["isActiveCategory"]
                      }
                      key={index}
                      onClick={() => setCategoryFilter(c.id)}
                      onTouchEnd={() => setCategoryFilter(c.id)}
                    >
                      {c.name}{" "}
                      <span>
                        ({photos.filter((p) => p.category?.id === c.id).length})
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
        <div className={!isProductPage && styles["Gallery"]}>
          <div className={styles["GalleryImageContainer"]}>
            {filteredPhotos.map(
              (
                image: { url: string; category?: { name: string } },
                i: React.Key
              ) => {
                return (
                  <div key={i} className={styles["GalleryImageWrapper"]}>
                    <div
                      className={styles["GalleryImage"]}
                      onClick={() =>
                        setOpenSecond({
                          state: true,
                          index: Number(i),
                        })
                      }
                    >
                      <CloudinaryImage
                        src={image.url}
                        transformations={["h_182"]}
                      />
                    </div>
                    <div className={styles["ImageCategoryName"]}>
                      {image.category?.name}
                    </div>
                  </div>
                );
              }
            )}
          </div>
          {categoryFilter === "videos" && (
            <div className={styles.VideosSection}>
              {videos.map((m, index) => {
                return (
                  <div className={styles.VideoHolder} key={index}>
                    <CloudinaryVideo
                      src={m}
                      muted
                      playsInline
                      controls
                      transformations={["h_200"]}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
      <Modal
        open={openSecond.state}
        onClose={() => {
          setOpenSecond({
            state: false,
            index: undefined,
          });
        }}
        classNames={{
          overlay: styles["secondGalleryOverlay"],
          modal: styles["secondModal"],
        }}
        center
      >
        <Swiper
          pagination={{
            type: "fraction",
          }}
          navigation={true}
          modules={[Pagination, Navigation]}
          className={styles["mySwiper"]}
          initialSlide={openSecond.index}
          spaceBetween={500}
        >
          {filteredPhotos.map((image: { url: string }, i: React.Key) => {
            return (
              <SwiperSlide className={styles["ImageModal"]} key={i}>
                <CloudinaryImage
                  src={image.url}
                  transformations={["h_500", "c_fill"]}
                  objFit={"contain"}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Modal>
    </>
  );
};

export default PlanImageGallery;
